/**
 * Outcome of the tax authority check on `vat_id`. Left as a raw string here so
 * the store mirrors the API; `TAX_ID_VALIDATION_STATUS` names the values.
 */
type VatIdValidation = {
  last_attempt_at?: string;
  /** The tax id as the authority holds it, without country prefix. */
  normalized?: string;
  status?: string;
};

/** A VAT fee profile, as the `fee_profiles` reducer stores it. */
type VatFeeProfile = {
  _id?: string;
  business_name?: string;
  city?: string;
  country?: string;
  is_subregion?: boolean;
  state?: string;
  street?: string;
  tax_id_validation?: VatIdValidation;
  type?: string;
  unit?: string;
  vat_id?: string;
  vat_percent?: number;
  zip?: string;
};

/** One address slot of the `fee_profiles` reducer, with its request flags. */
type FeeProfileAddress = {
  data?: VatFeeProfile[];
  error?: unknown;
  fetchError?: unknown;
  hasFetched: boolean;
  hasPosted?: boolean;
  isFetching: boolean;
  isPosting: boolean;
  postError?: unknown;
  updateError?: unknown;
};

type FeeProfilesState = {
  fee_profiles: {
    billingAddress: FeeProfileAddress;
    /** Populated per-invoice by `fetchAddressById`, for the invoice PDFs. */
    invoiceFeeProfile?: {
      data?: VatFeeProfile;
      isFetching?: boolean;
    };
    vatAddress: FeeProfileAddress;
  };
};

/** Stable reference so `useSelector` doesn't re-render on every store update. */
const EMPTY_VAT_PROFILES: VatFeeProfile[] = [];

const selectBillingAddress = (state: FeeProfilesState) =>
  state.fee_profiles?.billingAddress;

/**
 * Guarded on `hasFetched` so a submit before the read completes creates a profile
 * rather than trying to update one that is not known yet.
 */
const selectBillingFeeProfileId = (state: FeeProfilesState) => {
  const billingAddress = selectBillingAddress(state);

  return billingAddress?.hasFetched
    ? billingAddress?.data?.[0]?._id
    : undefined;
};

const selectBillingAddressHasFetched = (state: FeeProfilesState) =>
  Boolean(selectBillingAddress(state)?.hasFetched);

/** Whether the host already has a billing address on file. */
const selectHasBillingAddress = (state: FeeProfilesState) =>
  0 < (selectBillingAddress(state)?.data?.length ?? 0);

/**
 * The fetched VAT profiles. Anything but a list reads as none: callers go
 * straight on to `.filter`, and the legacy reducer is shared with pages this
 * one cannot see.
 */
const selectVatProfiles = (state: FeeProfilesState) => {
  const vatProfiles = state.fee_profiles?.vatAddress?.data;

  return Array.isArray(vatProfiles) ? vatProfiles : EMPTY_VAT_PROFILES;
};

const selectVatProfilesHaveFetched = (state: FeeProfilesState) =>
  Boolean(state.fee_profiles?.vatAddress?.hasFetched);

const selectVatProfilesAreFetching = (state: FeeProfilesState) =>
  Boolean(state.fee_profiles?.vatAddress?.isFetching);

const selectVatProfilesFetchError = (state: FeeProfilesState) =>
  state.fee_profiles?.vatAddress?.fetchError;

/** The profile attached to one invoice; may be a deactivated address. */
const selectInvoiceFeeProfile = (state: FeeProfilesState) =>
  state.fee_profiles?.invoiceFeeProfile?.data;

/** Business name off the first VAT profile, as the invoice PDFs print it. */
const selectVatBusinessName = (state: FeeProfilesState) =>
  selectVatProfiles(state)[0]?.business_name ?? "";

export {
  selectBillingAddressHasFetched,
  selectBillingFeeProfileId,
  selectHasBillingAddress,
  selectInvoiceFeeProfile,
  selectVatBusinessName,
  selectVatProfiles,
  selectVatProfilesAreFetching,
  selectVatProfilesFetchError,
  selectVatProfilesHaveFetched,
};

export type {
  FeeProfileAddress,
  FeeProfilesState,
  VatFeeProfile,
  VatIdValidation,
};
